import React, { useId } from "react";
import { LuChevronDown } from "react-icons/lu";
import { cn } from "./cn";

const controlClasses = (invalid?: boolean) =>
  cn(
    "w-full rounded-control border bg-surface px-3.5 text-body text-fg transition-shadow placeholder:text-fg-subtle",
    "focus:border-primary focus:outline-none focus:shadow-focus",
    "disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-fg-subtle",
    invalid ? "border-danger" : "border-line-strong"
  );

type InvalidProp = { invalid?: boolean };

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & InvalidProp>(
  ({ invalid, className, ...rest }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(controlClasses(invalid), "h-control-touch sm:h-control", className)}
      {...rest}
    />
  )
);
Input.displayName = "Input";

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & InvalidProp
>(({ invalid, className, ...rest }, ref) => (
  <textarea
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(controlClasses(invalid), "min-h-[88px] py-2", className)}
    {...rest}
  />
));
Textarea.displayName = "Textarea";

// Select nativo estilizado: é o mais confiável no celular (usa o seletor do sistema).
export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & InvalidProp>(
  ({ invalid, className, children, ...rest }, ref) => (
    <div className={cn("relative", className)}>
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(controlClasses(invalid), "h-control-touch appearance-none pr-9 sm:h-control")}
        {...rest}
      >
        {children}
      </select>
      <LuChevronDown
        size={16}
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-muted"
      />
    </div>
  )
);
Select.displayName = "Select";

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: React.ReactNode;
}

export const Checkbox: React.FC<CheckboxProps> = ({ label, className, ...rest }) => (
  <label
    className={cn(
      "inline-flex min-h-control-touch cursor-pointer items-center gap-2 text-body text-fg sm:min-h-0",
      rest.disabled && "cursor-not-allowed opacity-50",
      className
    )}
  >
    <input type="checkbox" className="h-4 w-4 shrink-0 accent-[rgb(var(--primary))]" {...rest} />
    {label}
  </label>
);

interface FieldProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  /** Um único Input, Select ou Textarea — recebe id e aria-describedby daqui. */
  children: React.ReactElement;
}

export const Field: React.FC<FieldProps> = ({ label, hint, error, className, children }) => {
  const id = useId();
  const descriptionId = `${id}-descricao`;
  const description = error || hint;

  const control = React.cloneElement(children, {
    id,
    "aria-describedby": description ? descriptionId : undefined,
    invalid: Boolean(error) || children.props.invalid,
  });

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-label font-semibold text-fg-secondary">
        {label}
      </label>
      {control}
      {description && (
        <p id={descriptionId} className={cn("text-caption", error ? "text-danger" : "text-fg-muted")}>
          {description}
        </p>
      )}
    </div>
  );
};
